/**
 * VendorListReport (LISTS-VENDORS, on the universal engine). Every vendor as a
 * flat, sortable, filterable list — the same look/feel/toolbar as the Invoice,
 * PO and Customer lists: pick columns, filter by any field, group (by State /
 * Terms / City), save views, export CSV, click a row to peek. Read-only ESC
 * vendor master (mirror_vendor) per DEC-22 — creating/editing vendors stays on
 * the Vendors operational screen (reachable from the command palette / Ctrl-K).
 *
 * Competitor frame: ServiceTitan / QuickBooks vendor lists — browse + filter the
 * supplier book, drill one. Adopted onto our one list engine so it matches the
 * rest of Reports.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Truck, X, ArrowRight, Phone, Mail, Globe, MapPin } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import {
  ListSearch, ViewsMenu, CsvButton, downloadCsv, pillSelectCls,
  useSort, useColumns, ColumnsButton, ListTable, FiltersButton, FilterChips, applyFilters,
  type ListField, type FilterCond,
} from "./listCore";

interface Row {
  vendor_code: string;
  name: string | null;
  first_name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  phone2: string | null;
  fax: string | null;
  email: string | null;
  web_url: string | null;
  terms: string | null;
  account: string | null;
  discount: number | null;
  tax_id: string | null;
  inactive: boolean;
  notes: string | null;
}

interface Filters { search: string; state: string; terms: string; activeOnly: boolean }
const EMPTY: Filters = { search: "", state: "", terms: "", activeOnly: true };
const COLS = "vendor_code,name,first_name,address1,address2,city,state,zip,phone,phone2,fax,email,web_url,terms,account,discount,tax_id,inactive,notes";
const DISPLAY_CAP = 500;

const pct = (n: number | null) => (n == null || n === 0 ? "—" : `${n}%`);

export function VendorListReport() {
  const navigate = useNavigateWithBack();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [conds, setConds] = useState<FilterCond[]>([]);
  const [selected, setSelected] = useState<Row | null>(null);
  const sort = useSort<Row>("name", "asc");

  const fields: ListField<Row>[] = useMemo(() => [
    {
      id: "name", type: "text", filterable: true, col: "name", label: "Vendor", value: (r) => r.name ?? r.vendor_code,
      render: (r) => (
        <span>
          <span className="font-medium text-slate-800">{r.name ?? r.vendor_code}</span>
          {r.inactive && <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold text-slate-400">inactive</span>}
        </span>
      ),
    },
    { id: "city", type: "text", filterable: true, groupable: true, col: "city", label: "City", value: (r) => r.city, render: (r) => <span className="text-sm text-slate-600">{r.city ?? "—"}</span> },
    { id: "state", type: "text", filterable: true, groupable: true, col: "state", label: "State", value: (r) => r.state },
    { id: "terms", type: "text", filterable: true, groupable: true, col: "terms", label: "Terms", value: (r) => r.terms, render: (r) => r.terms ? <span className="text-sm text-slate-600">{r.terms}</span> : <span className="text-slate-300">—</span> },
    { id: "phone", type: "text", filterable: true, col: "phone", label: "Phone", value: (r) => r.phone, render: (r) => r.phone ? <a href={`tel:${r.phone}`} className="text-brand-700 hover:underline">{r.phone}</a> : <span className="text-slate-300">—</span> },
    { id: "email", type: "text", filterable: true, col: "email", label: "Email", value: (r) => r.email, render: (r) => r.email ?? <span className="text-slate-300">—</span> },
    { id: "account", type: "text", filterable: true, col: "account", label: "Account #", value: (r) => r.account, defaultVisible: false },
    { id: "discount", numeric: true, type: "number", filterable: true, col: "discount", label: "Discount", value: (r) => Number(r.discount) || 0, render: (r) => <span className="tabular-nums text-slate-600">{pct(r.discount)}</span>, defaultVisible: false },
    { id: "tax_id", type: "text", filterable: true, col: "tax_id", label: "Tax ID", value: (r) => r.tax_id, defaultVisible: false },
    { id: "vendor_code", type: "text", filterable: true, col: "vendor_code", label: "Code", value: (r) => r.vendor_code, render: (r) => <span className="font-mono text-xs text-slate-500">{r.vendor_code}</span>, defaultVisible: false },
    { id: "zip", type: "text", filterable: true, groupable: true, col: "zip", label: "Zip", value: (r) => r.zip, defaultVisible: false },
    { id: "address1", type: "text", filterable: true, col: "address1", label: "Address", value: (r) => r.address1, defaultVisible: false },
    { id: "first_name", type: "text", filterable: true, col: "first_name", label: "Contact", value: (r) => r.first_name, defaultVisible: false },
    { id: "fax", type: "text", filterable: true, col: "fax", label: "Fax", value: (r) => r.fax, defaultVisible: false },
    { id: "web_url", type: "text", filterable: true, col: "web_url", label: "Website", value: (r) => r.web_url, defaultVisible: false },
    { id: "active", type: "bool", filterable: true, col: "inactive", label: "Status", value: (r) => (r.inactive ? "Inactive" : "Active"), groupable: true, defaultVisible: false },
  ], []);
  const cols = useColumns("vendors", fields);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["list_vendors", filters.activeOnly, filters.state, filters.terms, filters.search, conds],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ rows: Row[]; count: number }> => {
      let q = supabase.from("mirror_vendor").select(COLS, { count: "exact" });
      if (filters.activeOnly) q = q.eq("inactive", false);
      if (filters.state.trim()) q = q.ilike("state", filters.state.trim());
      if (filters.terms.trim()) q = q.ilike("terms", `%${filters.terms.trim()}%`);
      const s = filters.search.replace(/[%,()*]/g, " ").trim();
      if (s) q = q.or(`name.ilike.*${s}*,vendor_code.ilike.*${s}*,city.ilike.*${s}*`);
      q = applyFilters(q, conds, fields as ListField<unknown>[]);
      const { data, count, error } = await q.order("name", { nullsFirst: false }).limit(DISPLAY_CAP);
      if (error) throw error;
      return { rows: (data ?? []) as Row[], count: count ?? 0 };
    },
  });
  const rows = data?.rows ?? [];
  const count = data?.count ?? 0;
  const dirty = filters.search || filters.state || filters.terms || !filters.activeOnly;

  function exportCsv() {
    const shown = cols.visible.map((id) => fields.find((x) => x.id === id)).filter(Boolean) as ListField<Row>[];
    downloadCsv(
      `vendors-${new Date().toISOString().slice(0, 10)}.csv`,
      shown.map((c) => c.label),
      rows.map((r) => shown.map((c) => { const v = c.value(r); return v == null ? "" : String(v); })),
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <ListSearch value={filters.search} onChange={(v) => setFilters({ ...filters, search: v })} placeholder="Search vendor, code, city…" />
          <FiltersButton fields={fields} conds={conds} onChange={setConds} />
          <ColumnsButton fields={fields} cols={cols} />
          <CsvButton onClick={exportCsv} />
          <ViewsMenu entity="vendors" currentFilters={filters as unknown as Record<string, unknown>} onApply={(f) => setFilters({ ...EMPTY, ...(f as Partial<Filters>) })} />
          {/* This list is the read-only BROWSE view; creating/editing vendors
              lives on the operational screen (Seth 2026-08-17: "I have no
              ability to do anything other than view" — the door was only in
              Ctrl-K; now it's here too). */}
          <button
            type="button"
            onClick={() => navigate("/vendors")}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand-700 px-3 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
          >
            <Truck className="h-3.5 w-3.5" /> Open Vendors manager
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} placeholder="State" className={pillSelectCls} />
          <input value={filters.terms} onChange={(e) => setFilters({ ...filters, terms: e.target.value })} placeholder="Terms" className={pillSelectCls} />
          <label className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-xs font-medium text-slate-600">
            <input type="checkbox" checked={filters.activeOnly} onChange={(e) => setFilters({ ...filters, activeOnly: e.target.checked })} className="h-3.5 w-3.5 accent-brand-700" /> Active only
          </label>
          {dirty && <button onClick={() => setFilters(EMPTY)} className="text-xs font-medium text-slate-400 hover:text-slate-600">Clear</button>}
          {conds.length > 0 && <FilterChips conds={conds} fields={fields} onChange={setConds} />}
          <div className="ml-auto whitespace-nowrap text-sm tabular-nums text-slate-500">
            <strong className="text-slate-800">{count.toLocaleString()}</strong> vendors{isFetching ? " · …" : ""}{count > DISPLAY_CAP ? ` · first ${DISPLAY_CAP}` : ""}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : isError ? (
        <Card className="p-6"><p className="text-sm text-red-600">{(error as Error)?.message}</p></Card>
      ) : (
        <div className="flex items-start gap-3">
          <Card className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
              <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Truck className="h-3.5 w-3.5" /></span>
              <span className="text-[14.5px] font-bold tracking-tight text-slate-900">Vendors</span>
              <InfoTip label="How this list works">Read-only vendor list from ESC. Click a row to see the vendor; create or edit vendors in the Vendors manager. Group by State or Terms in the Fields menu.</InfoTip>
              <span className="ml-auto text-xs tabular-nums text-slate-500">{count.toLocaleString()} match{isFetching ? " · …" : ""}{count > DISPLAY_CAP ? ` · showing first ${DISPLAY_CAP}` : ""}</span>
            </div>
            <div className="max-h-[68vh] overflow-auto">
              <ListTable
                rows={rows}
                fields={fields}
                cols={cols}
                sort={sort}
                getKey={(r) => r.vendor_code}
                onRowClick={(r) => setSelected(r)}
                selectedKey={selected?.vendor_code ?? null}
                emptyText="No vendors match — clear a filter or include inactive."
              />
            </div>
          </Card>
          {selected && <VendorDetailsPanel row={selected} onClose={() => setSelected(null)} onOpen={() => navigate("/vendors")} />}
        </div>
      )}
    </div>
  );
}

function VendorDetailsPanel({ row, onClose, onOpen }: { row: Row; onClose: () => void; onOpen: () => void }) {
  const cityLine = [row.city, row.state].filter(Boolean).join(", ");
  const facts: [string, string | null][] = [
    ["Terms", row.terms],
    ["Account #", row.account],
    ["Discount", row.discount ? `${row.discount}%` : null],
    ["Tax ID", row.tax_id],
    ["Code", row.vendor_code],
  ];
  return (
    <div className="w-80 shrink-0">
      <Card className="overflow-hidden">
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-slate-900">{row.name ?? row.vendor_code}</div>
            {row.first_name && <div className="text-[11px] text-slate-400">{row.first_name}</div>}
          </div>
          <button onClick={onClose} title="Close" className="shrink-0 text-slate-300 transition-colors hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 p-4">
          {(row.address1 || cityLine) && (
            <div className="flex items-start gap-2 text-sm leading-snug text-slate-700">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <span>
                {row.address1 && <span className="block">{row.address1}</span>}
                {(cityLine || row.zip) && <span className="block text-slate-500">{cityLine}{row.zip ? ` ${row.zip}` : ""}</span>}
              </span>
            </div>
          )}
          {row.phone && <a href={`tel:${row.phone}`} className="flex items-center gap-2 text-sm font-medium text-brand-700 hover:underline"><Phone className="h-3.5 w-3.5 text-slate-400" />{row.phone}</a>}
          {row.email && <div className="flex items-center gap-2 truncate text-sm text-slate-600"><Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />{row.email}</div>}
          {row.web_url && <div className="flex items-center gap-2 truncate text-sm text-slate-600"><Globe className="h-3.5 w-3.5 shrink-0 text-slate-400" />{row.web_url}</div>}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-slate-100 pt-3">
            {facts.filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="min-w-0">
                <div className="text-[11.5px] font-semibold text-slate-500">{k}</div>
                <div className="truncate text-xs text-slate-700">{v}</div>
              </div>
            ))}
          </div>
          {row.notes && <div className="rounded-md bg-slate-50 px-2.5 py-2 text-xs text-slate-600">{row.notes}</div>}
          <button onClick={onOpen} className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700">
            Open in Vendors <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </Card>
    </div>
  );
}
